import React from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Bot, ThumbsUp, ThumbsDown, RefreshCw, AlertCircle } from 'lucide-react';
import { colors } from '../../theme/colors';

const ContentSection = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const thinkingAnimation = keyframes`
  0%  {box-shadow: 6px 0 #000, -6px 0 #0002;background: #000 }
  33% {box-shadow: 6px 0 #000, -6px 0 #0002;background: #0002}
  66% {box-shadow: 6px 0 #0002,-6px 0 #000; background: #0002}
  100%{box-shadow: 6px 0 #0002,-6px 0 #000; background: #000 }
`;

const FieldAnalysisSection = ({ 
  isAnalyzing,
  analysisError,
  analysisResult,
  onAnalyze,
  renderStatusBar
}) => {
  const renderAnalysisResult = () => {
    if (!analysisResult) return null;
    
    // Filter out LLM analysis error messages
    const filteredResult = analysisResult
      .split('\n')
      .filter(line => {
        const trimmedLine = line.trim();
        // Filter out error messages
        return !trimmedLine.includes('LLM analysis failed:') &&
               !trimmedLine.includes('Could not clean JSON response:') &&
               !trimmedLine.includes('Extra data: line') &&
               !trimmedLine.includes('Raw response:') &&
               !trimmedLine.startsWith('Raw response:') &&
               !trimmedLine.includes('LLM-based match: LLM analysis failed') &&
               !trimmedLine.includes('JSON parsing failed') &&
               !trimmedLine.includes('All JSON parsing attempts failed');
      })
      .join('\n');
    
    const lines = filteredResult.split('\n').filter(line => line.trim());
    
    // If no content remains after filtering, show no analysis available
    if (lines.length === 0) {
      return (
        <div style={{
          textAlign: 'center',
          padding: '2rem',
          color: '#6b7280',
          fontSize: '0.875rem'
        }}>
          No analysis available - LLM analysis encountered errors
        </div>
      );
    }
    
    let whyMatch = '';
    let whyNotMatch = '';
    let recommendation = '';
    
    let currentSection = '';
    for (const line of lines) {
      if (line.includes('**Why it might match:**')) {
        currentSection = 'match';
        whyMatch = line.replace('**Why it might match:**', '').trim();
      } else if (line.includes('**Why it might not match:**')) {
        currentSection = 'notMatch';
        whyNotMatch = line.replace('**Why it might not match:**', '').trim();
      } else if (line.includes('**Recommendation:**')) {
        currentSection = 'recommendation';
        recommendation = line.replace('**Recommendation:**', '').trim();
      } else if (line.trim() && currentSection) {
        if (currentSection === 'match') {
          whyMatch += (whyMatch ? ' ' : '') + line.trim();
        } else if (currentSection === 'notMatch') {
          whyNotMatch += (whyNotMatch ? ' ' : '') + line.trim();
        } else if (currentSection === 'recommendation') {
          recommendation += (recommendation ? ' ' : '') + line.trim();
        }
      }
    }

    return (
      <>
        {/* Competing sections */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          {/* Why it might match */}
          <div style={{
            background: 'white',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            padding: '1rem',
            position: 'relative'
          }}>
            <div style={{
              position: 'absolute',
              top: '-8px',
              left: '1rem',
              background: 'black',
              color: 'white',
              padding: '0.25rem 0.75rem',
              borderRadius: '12px',
              fontSize: '0.75rem',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem'
            }}>
              <div style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                background: '#22c55e',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <ThumbsUp size={12} style={{ color: 'white' }} />
              </div>
              Why it might match
            </div>
            <div style={{
              marginTop: '0.5rem',
              fontSize: '0.875rem',
              lineHeight: '1.5',
              color: '#374151'
            }}>
              {whyMatch ? (
                <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                  {whyMatch.split('- ').filter(item => item.trim()).map((item, index) => (
                    <li key={index} style={{ marginBottom: '0.25rem' }}>
                      {item.trim()}
                    </li>
                  ))}
                </ul>
              ) : (
                'No reasons provided'
              )}
            </div>
          </div>

          {/* Why it might not match */}
          <div style={{
            background: 'white',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            padding: '1rem',
            position: 'relative'
          }}>
            <div style={{
              position: 'absolute',
              top: '-8px',
              left: '1rem',
              background: 'black',
              color: 'white',
              padding: '0.25rem 0.75rem',
              borderRadius: '12px',
              fontSize: '0.75rem',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem'
            }}>
              <div style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                background: '#ef4444',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <ThumbsDown size={12} style={{ color: 'white' }} />
              </div>
              Why it might not match
            </div>
            <div style={{
              marginTop: '0.5rem',
              fontSize: '0.875rem',
              lineHeight: '1.5',
              color: '#374151'
            }}>
              {whyNotMatch ? (
                <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                  {whyNotMatch.split('- ').filter(item => item.trim()).map((item, index) => (
                    <li key={index} style={{ marginBottom: '0.25rem' }}>
                      {item.trim()}
                    </li>
                  ))}
                </ul>
              ) : (
                'No concerns provided'
              )}
            </div>
          </div>
        </div>

        {/* Recommendation */}
        {recommendation && (
          <div style={{
            background: 'white',
            border: '1px solid #3b82f6',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1rem',
            position: 'relative'
          }}>
            <div style={{
              position: 'absolute',
              top: '-8px',
              left: '1rem',
              background: '#3b82f6',
              color: 'white',
              padding: '0.25rem 0.75rem',
              borderRadius: '12px',
              fontSize: '0.75rem',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem'
            }}>
              <RefreshCw size={12} style={{ color: 'white' }} />
              Recommendation
            </div>
            <div style={{
              marginTop: '0.5rem',
              fontSize: '0.875rem',
              lineHeight: '1.5',
              color: '#374151'
            }}>
              {recommendation}
            </div>
          </div>
        )}

        {/* Re-analyze button */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '1.5rem'
        }}>
          <button
            onClick={onAnalyze}
            disabled={isAnalyzing}
            style={{
              padding: '0.75rem 1.5rem',
              background: '#f3f4f6',
              color: '#6b7280',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#e5e7eb';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = '#f3f4f6';
            }}
          >
            Re-analyze
          </button>
        </div>
      </>
    );
  };

  return (
    <>
      {renderStatusBar && renderStatusBar()}
      <ContentSection>
        {isAnalyzing ? (
          <>
            <div style={{
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '2rem',
              textAlign: 'center'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.75rem',
                marginBottom: '1rem'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: '#f59e0b',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Bot size={18} style={{ color: 'white' }} />
                </div>
                <span style={{ fontSize: '1rem', fontWeight: '600', color: '#374151' }}>
                  Thinking
                </span>
                <div
                  style={{
                    width: '4px',
                    height: '4px',
                    borderRadius: '50%',
                    background: '#000',
                    boxShadow: '6px 0 #000, -6px 0 #0002',
                    animation: `${thinkingAnimation} 1s infinite linear alternate`
                  }}
                />
              </div>
            </div>
          </>
        ) : analysisError ? (
          <div style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '1.5rem',
            textAlign: 'center'
          }}>
            <div style={{ color: '#dc2626', fontSize: '0.875rem', marginBottom: '1rem' }}>
              Error: {analysisError}
            </div>
            <button
              onClick={onAnalyze}
              disabled={isAnalyzing}
              style={{
                padding: '0.5rem 1rem',
                background: colors.primary,
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Try Again
            </button>
          </div>
        ) : (
          <div>
            {renderAnalysisResult()}
          </div>
        )}
      </ContentSection>
    </>
  );
};

export default FieldAnalysisSection;
